/**
 * 69.74 step 2b (DECISIONS 302; the coordinator: "a plain pad (a slab, an H, spot marks)"): the helipad's look — a concrete
 * slab as the sim's (HELI_PAD: its spots in a row across it, `spotPeople` apart, `halfZPeople` deep), and on each spot a
 * painted ring and an H, the H's bar across the row (a helicopter parks nose to +z, the pad's front, toward the enemy).
 * Destroyed: the slab scorched and the paint burnt to soot. Metres, a person 1.8 tall; the slab's top at `deck`. The parked
 * helicopters are the action host's (game/actionHost.ts, core/helipad.ts parkedHelis), not part of it.
 */
import * as THREE from 'three';
import { HELI_PAD } from '../constants/helicopter';
import { box, mat } from './vehicleModels';

const P = 1.8;
const CONCRETE = '#7c7a74', PAINT = '#e6e2d6', RING = '#d6ae3c', BURNT = '#3a3634', SOOT = '#262321';

/** The pad's measures (metres at a person 1.8): its width (the row), depth and slab height; a spot's ring and its H. */
export const HELIPAD_LOOK = {
  width: HELI_PAD.spots * HELI_PAD.spotPeople * P, depth: 2 * HELI_PAD.halfZPeople * P, deck: 0.1 * P,
  ringR: 0.4 * HELI_PAD.spotPeople * P, ringW: 0.45, hTall: 5.4, hWide: 3.6, stroke: 0.8, paint: 0.02,
} as const;

/** The pad as drawn: its slab (named `slab`, what its hit shape measures) and each of its HELI_PAD.spots spots' marks. */
export function buildHelipad(wrecked = false): THREE.Group {
  const L = HELIPAD_LOOK, root = new THREE.Group(), spots = HELI_PAD.spots;
  const slab = box(root, L.width, L.deck, L.depth, wrecked ? BURNT : CONCRETE, 0, L.deck / 2, 0);
  slab.name = 'slab';
  const y = L.deck + L.paint / 2, paint = wrecked ? SOOT : PAINT;
  for (let k = 0; k < spots; k++) {
    const x = (k - (spots - 1) / 2) * HELI_PAD.spotPeople * P;
    // the H: two uprights along the depth and the bar across the row
    box(root, L.stroke, L.paint, L.hTall, paint, x - L.hWide / 2, y, 0);
    box(root, L.stroke, L.paint, L.hTall, paint, x + L.hWide / 2, y, 0);
    box(root, L.hWide - L.stroke, L.paint, L.stroke, paint, x, y, 0);
    // the ring round it, flat on the slab facing up
    const ring = new THREE.Mesh(new THREE.RingGeometry(L.ringR - L.ringW, L.ringR, 40), mat(wrecked ? SOOT : RING));
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(x, L.deck + L.paint / 2, 0);
    root.add(ring);
  }
  return root;
}
